import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Copy, Download, Share2, Check } from 'lucide-react';
import { toast } from 'sonner';

export const ArticleToolbar = ({ article, articleId }) => {
  const [copiedFormat, setCopiedFormat] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const authors = article?.authors || [];
  const title = article?.title || '';
  const journal = article?.journal || '';
  const year = article?.publication_year || '';
  const doi = article?.doi || '';

  const getAPA = () => {
    const auth = authors.length > 0 ? `${authors[0]}${authors.length > 1 ? ' et al.' : ''}` : 'Author(s)';
    return `${auth} (${year || 'n.d.'}). ${title}. ${journal}.${doi ? ` https://doi.org/${doi}` : ''}`;
  };

  const getVancouver = () => {
    const auth = authors.length > 0 ? `${authors.slice(0, 3).join(', ')}${authors.length > 3 ? ', et al' : ''}` : 'Author(s)';
    return `${auth}. ${title}. ${journal}. ${year}.${doi ? ` DOI: ${doi}` : ''}`;
  };

  const handleCopy = async (format) => {
    const text = format === 'apa' ? getAPA() : getVancouver();
    try {
      await navigator.clipboard.writeText(text);
      setCopiedFormat(format);
      toast.success(`${format.toUpperCase()} citation copied`);
      setTimeout(() => setCopiedFormat(null), 2000);
    } catch { toast.error('Failed to copy'); }
  };

  const canonicalUrl = `https://evidentica.net/article/${articleId}`;
  const shareText = `${title} - ${journal} (${year})`;
  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(canonicalUrl);

  return (
    <div className="border border-slate-200 rounded-lg p-4 mb-6 bg-white" data-testid="article-toolbar">
      {/* Citation Copy */}
      <div className="mb-4">
        <p className="text-sm font-semibold text-slate-700 mb-2">Cite this article</p>
        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <p className="flex-1 text-xs bg-slate-50 p-2.5 rounded border border-slate-200 font-mono leading-relaxed" data-testid="apa-citation">{getAPA()}</p>
            <Button variant="ghost" size="sm" onClick={() => handleCopy('apa')} className="shrink-0 h-8" data-testid="copy-apa-btn">
              {copiedFormat === 'apa' ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
              <span className="ml-1 text-xs">APA</span>
            </Button>
          </div>
          <div className="flex items-start gap-2">
            <p className="flex-1 text-xs bg-slate-50 p-2.5 rounded border border-slate-200 font-mono leading-relaxed" data-testid="vancouver-citation">{getVancouver()}</p>
            <Button variant="ghost" size="sm" onClick={() => handleCopy('vancouver')} className="shrink-0 h-8" data-testid="copy-vancouver-btn">
              {copiedFormat === 'vancouver' ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
              <span className="ml-1 text-xs">VAN</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Export + Share row */}
      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
        <a href={`${API_URL}/api/articles/${articleId}/export/bibtex`} download data-testid="export-bibtex-btn">
          <Button variant="outline" size="sm" className="h-8 text-xs">
            <Download className="mr-1 h-3 w-3" /> BibTeX
          </Button>
        </a>
        <a href={`${API_URL}/api/articles/${articleId}/export/ris`} download data-testid="export-ris-btn">
          <Button variant="outline" size="sm" className="h-8 text-xs">
            <Download className="mr-1 h-3 w-3" /> RIS
          </Button>
        </a>

        <div className="ml-auto relative">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setShowShare(!showShare)} data-testid="share-toggle-btn">
            <Share2 className="mr-1 h-3 w-3" /> Share
          </Button>
          {showShare && (
            <div className="absolute right-0 top-10 z-10 bg-white border border-slate-200 rounded-lg shadow-lg p-2 flex gap-1.5" data-testid="share-dropdown">
              <a href={`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`} target="_blank" rel="noopener noreferrer" data-testid="share-twitter">
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Twitter/X">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </Button>
              </a>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`} target="_blank" rel="noopener noreferrer" data-testid="share-linkedin">
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="LinkedIn">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </Button>
              </a>
              <a href={`https://www.researchgate.net/go.Share.html?url=${encodedUrl}&title=${encodedText}`} target="_blank" rel="noopener noreferrer" data-testid="share-researchgate">
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="ResearchGate">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19.586 0c-2.39 0-4.323 1.932-4.323 4.323 0 .405.057.796.162 1.167a12.07 12.07 0 0 1-3.41-.988A12.084 12.084 0 0 1 8.006.962C6.907.352 5.793 0 4.696 0 2.104 0 0 2.104 0 4.696c0 1.543.747 2.912 1.9 3.766a4.627 4.627 0 0 0-.221 1.412c0 2.59 2.104 4.696 4.696 4.696a4.67 4.67 0 0 0 2.746-.893c.59.56 1.253 1.048 1.98 1.445 1.334.729 2.784 1.166 4.315 1.273v3.108c0 .69.224 1.3.604 1.79-.89.503-1.93.707-3.117.707-3.395 0-6.166-2.771-6.166-6.166h-2.82c0 4.948 4.038 8.987 8.986 8.987 1.946 0 3.734-.621 5.2-1.673A4.304 4.304 0 0 0 24 19.586V4.323C24 1.932 22.068 0 19.586 0z"/></svg>
                </Button>
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
